import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    CreateMentorDto,
    UpdateMentorDto,
    ApiResponse,
    PaginatedResponse,
    Profile
} from '../../../shared/types';
import axiosService from '../services/axios.service';

// Query key factory
export const mentorKeys = {
    all: ['mentors'] as const,
    crews: (crewSlug: string) => [...mentorKeys.all, 'crew', crewSlug] as const,
    list: (crewSlug: string, page?: number, limit?: number) =>
    [...mentorKeys.crews(crewSlug), 'list', { page, limit }] as const,
    recommended: (crewSlug: string, growthAreaIds: string[], page?: number, limit?: number) =>
    [...mentorKeys.crews(crewSlug), 'recommended', [...growthAreaIds].sort().join(','), { page, limit }] as const,
    detail: (crewSlug: string, mentorId: string) =>
    [...mentorKeys.crews(crewSlug), 'detail', mentorId] as const
};

// API functions
const mentorApi = {
    getMentors: async (crewSlug: string, page: number = 1, limit: number = 100): Promise<PaginatedResponse<Profile>> => {
        const { data } = await axiosService.get<PaginatedResponse<Profile>>(`/c/${crewSlug}/mentors`, {
            params: { page, limit }
        });
        return data;
    },

    getRecommendedMentors: async (crewSlug: string, growthAreaIds: string[], page: number = 1, limit: number = 20): Promise<PaginatedResponse<Profile>> => {
        const { data } = await axiosService.get<PaginatedResponse<Profile>>(`/c/${crewSlug}/mentors`, {
            params: { growthAreaIds: growthAreaIds.join(','), page, limit }
        });
        return data;
    },

    getMentor: async (crewSlug: string, mentorId: string): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.get<ApiResponse<Profile>>(`/c/${crewSlug}/mentors/${mentorId}`);
        return data;
    },

    createMentor: async (crewSlug: string, mentorData: CreateMentorDto): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.post<ApiResponse<Profile>>(`/c/${crewSlug}/mentors`, mentorData);
        return data;
    },

    updateMentor: async (crewSlug: string, mentorId: string, mentorData: UpdateMentorDto): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.put<ApiResponse<Profile>>(`/c/${crewSlug}/mentors/${mentorId}`, mentorData);
        return data;
    },

    addMentee: async (crewSlug: string, mentorId: string, menteeId: string): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.post<ApiResponse<Profile>>(`/c/${crewSlug}/mentors/${mentorId}/mentees`, {
            menteeId
        });
        return data;
    },

    removeMentee: async (crewSlug: string, mentorId: string, menteeId: string): Promise<ApiResponse<Profile>> => {
        const { data } = await axiosService.delete<ApiResponse<Profile>>(
            `/c/${crewSlug}/mentors/${mentorId}/mentees/${menteeId}`
        );
        return data;
    }
};

// React Query hooks
export const useMentors = (crewSlug: string, page: number = 1, limit: number = 100) => {
    return useQuery({
        queryKey: mentorKeys.list(crewSlug, page, limit),
        queryFn: () => mentorApi.getMentors(crewSlug, page, limit),
        enabled: !!crewSlug
    });
};

export const useRecommendedMentors = (crewSlug: string, growthAreaIds: string[], page: number = 1, limit: number = 20) => {
    return useQuery({
        queryKey: mentorKeys.recommended(crewSlug, growthAreaIds, page, limit),
        queryFn: () => mentorApi.getRecommendedMentors(crewSlug, growthAreaIds, page, limit),
        enabled: !!crewSlug && growthAreaIds.length > 0
    });
};

export const useMentor = (crewSlug: string, mentorId: string) => {
    return useQuery({
        queryKey: mentorKeys.detail(crewSlug, mentorId),
        queryFn: () => mentorApi.getMentor(crewSlug, mentorId),
        enabled: !!crewSlug && !!mentorId
    });
};

export const useCreateMentor = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (mentorData: CreateMentorDto) => mentorApi.createMentor(crewSlug, mentorData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};

export const useUpdateMentor = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorId, data }: { mentorId: string; data: UpdateMentorDto }) =>
            mentorApi.updateMentor(crewSlug, mentorId, data),
        onSuccess: (_, { mentorId }) => {
            queryClient.invalidateQueries({ queryKey: mentorKeys.detail(crewSlug, mentorId) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};

export const useAddMentee = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorId, menteeId }: { mentorId: string; menteeId: string }) =>
            mentorApi.addMentee(crewSlug, mentorId, menteeId),
        onSuccess: (_, { mentorId }) => {
            queryClient.invalidateQueries({ queryKey: mentorKeys.detail(crewSlug, mentorId) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};

export const useRemoveMentee = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorId, menteeId }: { mentorId: string; menteeId: string }) =>
            mentorApi.removeMentee(crewSlug, mentorId, menteeId),
        onSuccess: (_, { mentorId }) => {
            queryClient.invalidateQueries({ queryKey: mentorKeys.detail(crewSlug, mentorId) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
        }
    });
};

export interface MentorshipReviewSummary {
    count: number;
    overallAverage: number | null;
    averageCommunication: number | null;
    averageGuidance: number | null;
    averageKnowledge: number | null;
}

export const useMentorshipReviewSummary = (crewSlug: string, mentorProfileId: string | undefined) => {
    return useQuery({
        queryKey: [...mentorKeys.detail(crewSlug, mentorProfileId ?? ''), 'mentorship-reviews'],
        queryFn: async () => {
            const { data } = await axiosService.get<ApiResponse<MentorshipReviewSummary>>(
                `/c/${crewSlug}/mentors/${mentorProfileId}/mentorship-reviews`
            );
            return data.data;
        },
        enabled: !!crewSlug && !!mentorProfileId
    });
};
